
import device from 'bits/device'
import { ResizeObserver } from 'bits/global'
import { observeSizeChange } from 'bits/resize_observer'
import { Component, Prop, Vue } from 'bits/vue'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import { getAnchorFromCoordinates, getAnchorFromElement } from 'library/v4/components/OzPopoverModal.vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import OzWallCard from 'library/v4/components/OzWallCard.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import { throttle } from 'lodash-es'
import type { Library, WallCamelCase as Wall, Wall as WallSnakeCase } from 'types'
import type { CollectionKey } from 'types/collections'

const GAP_SIZE = 16
const MIN_CARD_WIDTH = 190

/**
 * @displayName Wall Card List
 */

@Component({
  components: { OzIcon, OzBaseButton, OzTextButton, ImageThumbnail, RelativeTime, OzWallCard },
})
export default class DashWallCardList extends Vue {
  OzTextButtonColorScheme = OzTextButtonColorScheme
  containerWidth = 1024
  numCardsPerRowHistory: number[] = []
  resizeObserver: ResizeObserver | null = null

  /**
   * Array of walls to display
   */
  @Prop({ default: [] }) walls!: Wall[] | WallSnakeCase[]

  /**
   * Array of libraries that user is part of to display
   */
  @Prop({ default: () => [] }) libraries!: Library[]

  /**
   * Active collection name
   */
  @Prop() activeCollectionKey!: CollectionKey

  /**
   * Whether or not to show more button
   */
  @Prop({ required: false, default: true }) xMore!: boolean

  /**
   * The timestamp type to show in the card.
   */
  @Prop({ default: 'updatedAt' }) dateAttribute!: string

  /**
   * target attribute for the link
   */
  @Prop({ default: '_blank' }) hrefTarget!: string

  get isTablet(): boolean {
    return device.tablet
  }

  get numOfColumns() {
    // In https://github.com/padlet/mozart/pull/14714, we used the scrollbar-overlay to prevent the scrollbar width
    // from affecting the calculation of numOfColumns.
    // Since `overflow-overlay` is deprecated, we can preemptively subtract the scrollbar width from the container width
    // to prevent it from affecting the calculation of numOfColumns.
    const SCROLLBAR_WIDTH = 14
    return Math.floor((this.containerWidth - SCROLLBAR_WIDTH + GAP_SIZE) / (MIN_CARD_WIDTH + GAP_SIZE))
  }

  get cardWidth() {
    return (this.containerWidth - (this.numOfColumns - 1) * GAP_SIZE) / this.numOfColumns
  }

  getLibrary(wall: WallSnakeCase | Wall): Library | null {
    if (!['combined_recents', 'favorites', 'combined_shared'].includes(String(this.activeCollectionKey?.indexKey))) {
      return null
    }
    const libraryId = 'libraryId' in wall ? wall.libraryId : wall.library_id
    return libraryId ? this.libraries[libraryId] : null
  }

  measureContainerWidth(_: HTMLElement, contentRect: DOMRectReadOnly): void {
    this.containerWidth = contentRect.width
  }

  mounted(): void {
    this.resizeObserver = observeSizeChange(
      this.$el as HTMLDivElement,
      throttle(this.measureContainerWidth, 100, { leading: true }),
    )
  }

  beforeDestroy(): void {
    this.resizeObserver?.unobserve(this.$el)
  }

  handleMoreButtonClick(wall: Wall, e: PointerEvent): void {
    const popoverAnchor = {
      position: 'side',
      ...getAnchorFromElement(e.target as HTMLElement),
    }
    this.$emit('wall-more', { wall, popoverAnchor })
  }

  handleMoreRightClick(wall: Wall, e: PointerEvent): void {
    const popoverAnchor = {
      position: 'side',
      ...getAnchorFromCoordinates(e.pageX, e.pageY),
    }
    this.$emit('wall-more', { wall, popoverAnchor })
  }
}
